import { useCallback, useEffect, useState } from 'react';
import {
  getAdminUsers, createUser, updateUser,
  getAdminTeams, createTeam as apiCreateTeam, updateTeam as apiUpdateTeam, deleteTeam as apiDeleteTeam
} from '../api/admin.js';
import { getTeamMembers, createTeamMember, updateTeamMember } from '../api/team.js';

const createEmptyManagerForm = () => ({
  email: '', name: '', password: '', role: 'MANAGER', teamId: null, isActive: true
});

/**
 * Admin/settings hook — manages admin users, teams,
 * stats, AI config, manager modal, and test lead operations.
 *
 * @param {object} params
 * @param {string|null} params.token
 * @param {boolean} params.isAdmin
 * @param {boolean} params.isTeamLead
 * @param {string} params.activeView
 * @param {import('@tanstack/react-query').QueryClient} params.queryClient
 * @param {function} params.navigate
 * @param {string|null} params.selectedLeadId
 * @param {function} params.setSelectedLeadId
 * @param {function} params.setIsDetailsOpen
 */
export function useAdminData ({
  token, isAdmin, isTeamLead, activeView,
  queryClient, navigate,
  selectedLeadId, setSelectedLeadId, setIsDetailsOpen
}) {

  const [adminUsers, setAdminUsers] = useState([]);
  const [adminTeams, setAdminTeams] = useState([]);
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [managerForm, setManagerForm] = useState(createEmptyManagerForm);
  const [isManagerModalOpen, setIsManagerModalOpen] = useState(false);
  const [editingManager, setEditingManager] = useState(null);

  const openManagerModal = useCallback(() => {
    setManagerForm(createEmptyManagerForm());
    setEditingManager(null);
    setIsManagerModalOpen(true);
  }, []);

  const closeManagerModal = useCallback(() => {
    setIsManagerModalOpen(false);
    setManagerForm(createEmptyManagerForm());
    setEditingManager(null);
  }, []);

  const handleEditManager = useCallback((user) => {
    setEditingManager(user);
    setManagerForm({
      email: user.email || '',
      name: user.name || '',
      password: '',
      role: user.role || 'MANAGER',
      teamId: user.teamId || null,
      isActive: user.isActive !== false
    });
    setIsManagerModalOpen(true);
  }, []);

  const handleManagerFormFieldChange = useCallback((field, value) => {
    setManagerForm(prev => ({ ...prev, [field]: value }));
  }, []);

  const loadAdminResources = useCallback(async () => {
    if (!isAdmin) return;
    setSettingsLoading(true);
    try {
      const results = await Promise.allSettled([
        getAdminUsers(),
        getAdminTeams()
      ]);
      const [usersRes, teamsRes] = results;

      if (usersRes.status === 'fulfilled') {
        setAdminUsers(usersRes.value);
      } else {
        console.error('[Admin] Failed to load users:', usersRes.reason);
      }
      if (teamsRes.status === 'fulfilled') {
        setAdminTeams(teamsRes.value);
      } else {
        console.error('[Admin] Failed to load teams:', teamsRes.reason);
      }

      setManagerForm(createEmptyManagerForm());
      setIsManagerModalOpen(false);
    } catch (err) {
      console.error('[Admin] Critical failure in loadAdminResources', err);
    } finally {
      setSettingsLoading(false);
    }
  }, [isAdmin]);

  const loadTeamLeadResources = useCallback(async () => {
    if (!isTeamLead) return;
    setSettingsLoading(true);
    try {
      const results = await Promise.allSettled([
        getTeamMembers()
      ]);
      const [membersRes] = results;

      if (membersRes.status === 'fulfilled') {
        const members = membersRes.value;
        setAdminUsers(members);

        const teamObj = members.find(m => m.team)?.team;
        if (teamObj) {
          setAdminTeams([teamObj]);
        }
      } else {
        console.error('[TeamLead] Failed to load members:', membersRes.reason);
      }
    } catch (err) {
      console.error('[TeamLead] Critical failure in loadTeamLeadResources', err);
    } finally {
      setSettingsLoading(false);
    }
  }, [isTeamLead]);

  const handleCreateManager = useCallback(async (event) => {
    if (event) event.preventDefault();
    const isEditing = Boolean(editingManager);
    if (!managerForm.email || (!isEditing && !managerForm.password)) return;
    setSettingsLoading(true);
    try {
      const payload = {
        email: managerForm.email,
        name: managerForm.name || undefined,
        role: managerForm.role || 'MANAGER',
        teamId: managerForm.teamId || null,
        isActive: managerForm.isActive !== false
      };
      if (managerForm.password) {
        payload.password = managerForm.password;
      }
      let result;
      if (isEditing) {
        if (isAdmin) {
          result = await updateUser(editingManager.id, payload);
        } else {

          result = await updateTeamMember(editingManager.id, payload);
        }
      } else if (isAdmin) {
        result = await createUser(payload);
      } else {

        const created = await createTeamMember({
          email: payload.email,
          password: managerForm.password,
          name: payload.name || undefined
        });
        result = created || {};
      }

      setAdminUsers(prev => {
        const filtered = prev.filter(item => item.id !== result.id);
        return [...filtered, result];
      });
      closeManagerModal();
    } catch (err) {
      console.error('Failed to save user', err);
    } finally {
      setSettingsLoading(false);
    }
  }, [editingManager, managerForm, isAdmin, closeManagerModal]);

  const handleToggleManagerActive = useCallback(async (managerId, active) => {
    setSettingsLoading(true);
    try {
      const updated = await updateUser(managerId, { isActive: active });
      setAdminUsers(prev => prev.map(user => (
        user.id === managerId && updated ? updated : user
      )));
    } catch (err) {
      console.error('Failed to update manager', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const handleCreateTeam = useCallback(async (teamData) => {
    setSettingsLoading(true);
    try {
      const team = await apiCreateTeam(teamData);
      if (team) {
        setAdminTeams(prev => [...prev, team]);
        const users = await getAdminUsers();
        setAdminUsers(users);
      }
    } catch (err) {
      console.error('Failed to create team', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const handleEditTeam = useCallback(async (teamId, teamData) => {
    setSettingsLoading(true);
    try {
      const team = await apiUpdateTeam(teamId, teamData);
      if (team) {
        setAdminTeams(prev => prev.map(t => t.id === teamId ? team : t));
        const users = await getAdminUsers();
        setAdminUsers(users);
      }
    } catch (err) {
      console.error('Failed to edit team', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const handleDeleteTeam = useCallback(async (teamId) => {
    if (!window.confirm('Удалить команду? Все участники будут отвязаны.')) return;
    setSettingsLoading(true);
    try {
      await apiDeleteTeam(teamId);
      setAdminTeams(prev => prev.filter(t => t.id !== teamId));
      const users = await getAdminUsers();
      setAdminUsers(users);
    } catch (err) {
      console.error('Failed to delete team', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!token) {
      setAdminUsers([]);
      setAdminTeams([]);
      setManagerForm(createEmptyManagerForm());
      return;
    }
    if (isAdmin) {
      loadAdminResources();
    } else if (isTeamLead) {
      loadTeamLeadResources();
    } else {
      setAdminUsers([]);
    }
  }, [token, isAdmin, isTeamLead, loadAdminResources, loadTeamLeadResources]);

  return {
    adminUsers, adminTeams, settingsLoading,
    managerForm,
    isManagerModalOpen, editingManager,
    openManagerModal, closeManagerModal, handleEditManager,
    handleManagerFormFieldChange,
    handleCreateManager, handleToggleManagerActive,
    handleCreateTeam, handleEditTeam, handleDeleteTeam,
  };
}
